type FullscreenCapableElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
  webkitRequestFullScreen?: () => Promise<void> | void;
  mozRequestFullScreen?: () => Promise<void> | void;
  msRequestFullscreen?: () => Promise<void> | void;
};

type FullscreenCapableDocument = Document & {
  webkitExitFullscreen?: () => Promise<void> | void;
  webkitCancelFullScreen?: () => Promise<void> | void;
  mozCancelFullScreen?: () => Promise<void> | void;
  msExitFullscreen?: () => Promise<void> | void;
  webkitFullscreenElement?: Element | null;
  webkitCurrentFullScreenElement?: Element | null;
  webkitIsFullScreen?: boolean;
  mozFullScreenElement?: Element | null;
  msFullscreenElement?: Element | null;
};

let transientFullscreenExitDeadline = 0;
const DEFAULT_FULLSCREEN_REQUEST_TIMEOUT_MS = 2000;
const ANDROID_FIREFOX_FULLSCREEN_REQUEST_TIMEOUT_MS = 1200;
type FullscreenAttemptResult = 'success' | 'error' | 'timeout';

/** 各模式为独立子串/版本号匹配，避免误伤普通 Chrome/Safari。 */
const FORCED_LANDSCAPE_FULLSCREEN_UA_PATTERNS: ReadonlyArray<RegExp> = [
  /\bMiuiBrowser\//i, // 小米浏览器
  /\bHuaweiBrowser\//i, // 华为浏览器
  /\bHeyTapBrowser\//i, // OPPO / realme / 部分 OnePlus 内置
  /\bVivoBrowser\//i, // vivo 浏览器
  /\bMZBrowser\//i, // 魅族浏览器
  /\bMQQBrowser\//i, // QQ 浏览器
  /\bUCBrowser\//i, // UC 浏览器
  /\bQuark\//i, // 夸克
];

/**
 * 部分国产 Android 内置浏览器在调用 Fullscreen API 时会强制横屏或异常退出全屏，体验不如保持非全屏的 100vh 弹层。
 * 匹配到此类 UA 时跳过 `requestPageFullscreen`（可按线上反馈扩展列表）。
 */
function isDocumentFullscreenDisabledForCurrentUserAgent(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const ua = navigator.userAgent;
  if (!/\bAndroid\b/i.test(ua)) {
    return false;
  }

  return FORCED_LANDSCAPE_FULLSCREEN_UA_PATTERNS.some((pattern) => pattern.test(ua));
}

function isAndroidFirefoxBrowser(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent;
  return /Android/i.test(userAgent) && /Firefox\/\d+/i.test(userAgent);
}

function hasRenderableDocumentFullscreenPresentation(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }

  const fullscreenDocument = document as FullscreenCapableDocument;

  /**
   * 仅用「是否存在全屏绑定的 DOM 节点」判断；若干 WebKit / 内置浏览器在异常退出后仍把
   * `webkitIsFullScreen` 卡在 true，`requestFullscreen` 会被我们自己的 early-return 拦住。
   */
  return Boolean(
    fullscreenDocument.fullscreenElement ||
      fullscreenDocument.webkitFullscreenElement ||
      fullscreenDocument.webkitCurrentFullScreenElement ||
      fullscreenDocument.mozFullScreenElement ||
      fullscreenDocument.msFullscreenElement,
  );
}

/** 是否与当前文档存在真实绑定的 fullscreen 节点（不依赖可能失真的 webkitIsFullScreen）。 */
export const isPageFullscreen = (): boolean => {
  return hasRenderableDocumentFullscreenPresentation();
};

export const suppressNextFullscreenAutoClose = (durationMs = 5000): void => {
  transientFullscreenExitDeadline = Date.now() + durationMs;
};

export const consumeTransientFullscreenExit = (): boolean => {
  if (Date.now() > transientFullscreenExitDeadline) {
    transientFullscreenExitDeadline = 0;
    return false;
  }

  transientFullscreenExitDeadline = 0;
  return true;
};

let requestPageFullscreenInFlight: Promise<boolean> | null = null;

const waitForFullscreenAttempt = async (
  invokeRequest: () => Promise<void> | void,
  timeoutMs: number,
): Promise<FullscreenAttemptResult> => {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return 'error';
  }

  return new Promise<FullscreenAttemptResult>((resolve) => {
    let settled = false;

    const cleanup = (result: FullscreenAttemptResult) => {
      if (settled) {
        return;
      }

      settled = true;
      window.clearTimeout(timeoutId);
      document.removeEventListener('fullscreenchange', handleChange);
      document.removeEventListener('webkitfullscreenchange', handleChange as EventListener);
      document.removeEventListener('mozfullscreenchange', handleChange as EventListener);
      document.removeEventListener('MSFullscreenChange', handleChange as EventListener);
      document.removeEventListener('fullscreenerror', handleError);
      document.removeEventListener('webkitfullscreenerror', handleError as EventListener);
      document.removeEventListener('mozfullscreenerror', handleError as EventListener);
      document.removeEventListener('MSFullscreenError', handleError as EventListener);
      resolve(result);
    };

    const handleChange = () => {
      cleanup(isPageFullscreen() ? 'success' : 'error');
    };

    const handleError = () => {
      cleanup('error');
    };

    document.addEventListener('fullscreenchange', handleChange);
    document.addEventListener('webkitfullscreenchange', handleChange as EventListener);
    document.addEventListener('mozfullscreenchange', handleChange as EventListener);
    document.addEventListener('MSFullscreenChange', handleChange as EventListener);
    document.addEventListener('fullscreenerror', handleError);
    document.addEventListener('webkitfullscreenerror', handleError as EventListener);
    document.addEventListener('mozfullscreenerror', handleError as EventListener);
    document.addEventListener('MSFullscreenError', handleError as EventListener);

    const timeoutId = window.setTimeout(() => {
      cleanup(isPageFullscreen() ? 'success' : 'timeout');
    }, timeoutMs);

    try {
      const requestResult = invokeRequest();

      if (requestResult && typeof (requestResult as Promise<void>).then === 'function') {
        void Promise.resolve(requestResult)
          .then(() => {
            if (isPageFullscreen()) {
              cleanup('success');
            }
          })
          .catch(() => {
            cleanup('error');
          });
        return;
      }

      if (isPageFullscreen()) {
        cleanup('success');
      }
    } catch {
      cleanup('error');
    }
  });
};

export const requestPageFullscreen = async (): Promise<boolean> => {
  if (typeof document === 'undefined') {
    return false;
  }

  if (requestPageFullscreenInFlight) {
    return requestPageFullscreenInFlight;
  }

  requestPageFullscreenInFlight = runRequestPageFullscreen();

  try {
    return await requestPageFullscreenInFlight;
  } finally {
    requestPageFullscreenInFlight = null;
  }
};

export const exitPageFullscreen = async (): Promise<void> => {
  if (typeof document === 'undefined') {
    return;
  }

  const fullscreenDocument = document as FullscreenCapableDocument;

  try {
    if (fullscreenDocument.exitFullscreen) {
      await fullscreenDocument.exitFullscreen();
      return;
    }

    if (fullscreenDocument.webkitExitFullscreen) {
      await fullscreenDocument.webkitExitFullscreen();
      return;
    }

    if (fullscreenDocument.webkitCancelFullScreen) {
      await fullscreenDocument.webkitCancelFullScreen();
      return;
    }

    if (fullscreenDocument.mozCancelFullScreen) {
      await fullscreenDocument.mozCancelFullScreen();
      return;
    }

    if (fullscreenDocument.msExitFullscreen) {
      await fullscreenDocument.msExitFullscreen();
    }
  } catch {
    // Ignore fullscreen exit failures so modal close is never blocked.
  }
};

async function reconcileStaleFullscreenApiSignals(): Promise<void> {
  const fullscreenDocument = document as FullscreenCapableDocument;
  const staleWebkitBadge =
    typeof fullscreenDocument.webkitIsFullScreen === 'boolean' &&
    fullscreenDocument.webkitIsFullScreen &&
    !hasRenderableDocumentFullscreenPresentation();
  if (staleWebkitBadge) {
    await exitPageFullscreen();
  }
}

async function runRequestPageFullscreen(): Promise<boolean> {
  await reconcileStaleFullscreenApiSignals();

  const startedInFullscreen = isPageFullscreen();

  if (startedInFullscreen) {
    return true;
  }

  if (isDocumentFullscreenDisabledForCurrentUserAgent()) {
    return false;
  }

  const rootElement = document.documentElement as FullscreenCapableElement | null;

  if (!rootElement) {
    return false;
  }

  const timeoutMs = isAndroidFirefoxBrowser()
    ? ANDROID_FIREFOX_FULLSCREEN_REQUEST_TIMEOUT_MS
    : DEFAULT_FULLSCREEN_REQUEST_TIMEOUT_MS;

  const finish = (ok: boolean): boolean => {
    if (ok) {
      tryLockPortraitOrientationAfterFullscreen();
    }
    return ok;
  };

  if (rootElement.requestFullscreen) {
    const fullscreenWithHiddenNavigation = await waitForFullscreenAttempt(
      () => rootElement.requestFullscreen({ navigationUI: 'hide' }),
      timeoutMs,
    );

    if (fullscreenWithHiddenNavigation === 'success') {
      return finish(true);
    }

    if (fullscreenWithHiddenNavigation === 'timeout') {
      return false;
    }

    const fallbackEntered =
      (await waitForFullscreenAttempt(() => rootElement.requestFullscreen(), timeoutMs)) ===
      'success';
    return finish(fallbackEntered);
  }

  if (rootElement.webkitRequestFullscreen) {
    return finish(
      (await waitForFullscreenAttempt(() => rootElement.webkitRequestFullscreen?.(), timeoutMs)) ===
        'success',
    );
  }

  if (rootElement.webkitRequestFullScreen) {
    return finish(
      (await waitForFullscreenAttempt(() => rootElement.webkitRequestFullScreen?.(), timeoutMs)) ===
        'success',
    );
  }

  if (rootElement.mozRequestFullScreen) {
    return finish(
      (await waitForFullscreenAttempt(() => rootElement.mozRequestFullScreen?.(), timeoutMs)) ===
        'success',
    );
  }

  if (rootElement.msRequestFullscreen) {
    return finish(
      (await waitForFullscreenAttempt(() => rootElement.msRequestFullscreen?.(), timeoutMs)) ===
        'success',
    );
  }

  return finish(false);
}

/**
 * 部分 Android 内置浏览器（如小米浏览器）会因自动横竖屏切换退出 Fullscreen API。
 * 在已成功进入全屏后尝试锁定竖屏（通常仅在全屏会话内生效；不支持时静默跳过）。
 */
function tryLockPortraitOrientationAfterFullscreen(): void {
  if (typeof window === 'undefined') {
    return;
  }

  type PortraitLockOrientation = ScreenOrientation & {
    lock?: (orientation: string) => Promise<void>;
  };

  const orientation =
    typeof screen !== 'undefined'
      ? (screen.orientation as PortraitLockOrientation | undefined)
      : undefined;

  if (!orientation?.lock) {
    return;
  }

  try {
    void orientation.lock('portrait').catch(() => {
      /* 浏览器拒绝锁定时忽略 */
    });
  } catch {
    /* lock 不可用时忽略 */
  }
}
